import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import { useAuth } from "./hooks/useAuth";

// Lazy-load every non-home route to slash initial bundle
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const AddStartup = lazy(() => import("./pages/AddStartup"));
const Login = lazy(() => import("./pages/Login"));
const Categories = lazy(() => import("./pages/Categories"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Sponsorship = lazy(() => import("./pages/Sponsorship"));
const SponsorshipComplete = lazy(() => import("./pages/SponsorshipComplete"));
const BadgeEmbed = lazy(() => import("./pages/BadgeEmbed"));
const About = lazy(() => import("./pages/About"));
const Alternatives = lazy(() => import("./pages/Alternatives"));
const AlternativeComparison = lazy(() => import("./pages/AlternativeComparison"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogEditor = lazy(() => import("./pages/BlogEditor"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const StackBuilder = lazy(() => import("./pages/StackBuilder"));
const TrustWall = lazy(() => import("./pages/TrustWall"));
const TrustWallSetup = lazy(() => import("./pages/TrustWallSetup"));
const TrustWallProduct = lazy(() => import("./pages/TrustWallProduct"));
const TrustWallSubmit = lazy(() => import("./pages/TrustWallSubmit"));
const TrustWallManage = lazy(() => import("./pages/TrustWallManage"));
const Launchpad = lazy(() => import("./pages/Launchpad"));
const LaunchpadSubmit = lazy(() => import("./pages/LaunchpadSubmit"));
const LaunchpadProduct = lazy(() => import("./pages/LaunchpadProduct"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// No spinner — index.html shows an inline skeleton on first paint, and route
// chunks are prefetched. A flash of nothing beats a spinner for perceived speed.
const PageFallback = () => null;

// Safety-net: if OAuth lands the user on the wrong path, forward them to redirectAfterLogin
const AuthRedirectHandler = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (loading || !user) return;
    const target = sessionStorage.getItem("redirectAfterLogin");
    if (target && target !== location.pathname) {
      sessionStorage.removeItem("redirectAfterLogin");
      navigate(target, { replace: true });
    }
  }, [user, loading, location.pathname, navigate]);
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthRedirectHandler />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/leaderboard" element={<Index />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/add" element={<AddStartup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/sponsorship" element={<Sponsorship />} />
            <Route path="/sponsorship/complete" element={<SponsorshipComplete />} />
            <Route path="/badge/:slug" element={<BadgeEmbed />} />
            <Route path="/about" element={<About />} />
            <Route path="/alternatives" element={<Alternatives />} />
            <Route path="/alternatives/:competitor" element={<AlternativeComparison />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/new" element={<BlogEditor />} />
            <Route path="/blog/edit/:slug" element={<BlogEditor />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/stack-builder" element={<StackBuilder />} />
            <Route path="/trustwall" element={<TrustWall />} />
            <Route path="/trustwall/setup" element={<TrustWallSetup />} />
            <Route path="/trustwall/submit/:token" element={<TrustWallSubmit />} />
            <Route path="/trustwall/manage/:slug" element={<TrustWallManage />} />
            <Route path="/trustwall/:slug" element={<TrustWallProduct />} />
            <Route path="/launchpad" element={<Launchpad />} />
            <Route path="/launchpad/submit" element={<LaunchpadSubmit />} />
            <Route path="/launchpad/:slug" element={<LaunchpadProduct />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
